import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { WorkloadsWastedGpuCostImpactTopItem } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const WORKLOADS_GPU_WASTE_COST_IMPACT_QUERY_KEY =
  'workloads-gpu-waste-cost-impact';

type UseWorkloadsGpuWasteCostImpactQueryProps = {
  clusterIds: string[];
  startTime: string;
  endTime: string;
  useListingPrices: boolean;
  pageLimit?: number;
  enabled?: boolean;
};

type UseWorkloadsGpuWasteCostImpactQuery = {
  data?: WorkloadsWastedGpuCostImpactTopItem[];
  isLoading: boolean;
  error?: unknown;
  refetch?: () => void;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  isFetching: boolean;
  count: number;
};

const getWorkloadsGpuWasteCostImpactQueryKey = ({
  clusterIds,
  startTime,
  endTime,
  useListingPrices,
  pageLimit,
}: Pick<
  UseWorkloadsGpuWasteCostImpactQueryProps,
  'clusterIds' | 'startTime' | 'endTime' | 'useListingPrices'
> & { pageLimit: number }) =>
  [
    WORKLOADS_GPU_WASTE_COST_IMPACT_QUERY_KEY,
    clusterIds,
    startTime,
    endTime,
    useListingPrices,
    pageLimit,
  ] as const;

const getWorkloadsGpuWasteCostImpactQueryOptions = ({
  clusterIds,
  startTime,
  endTime,
  useListingPrices,
  enabled = true,
  pageLimit = 5,
}: UseWorkloadsGpuWasteCostImpactQueryProps) =>
  infiniteQueryOptions({
    queryKey: getWorkloadsGpuWasteCostImpactQueryKey({
      clusterIds,
      startTime,
      endTime,
      useListingPrices,
      pageLimit,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } =
        await apiClient.costReport.getWorkloadsWastedGPUCostImpact({
          filter: { clusterIds },
          startTime,
          endTime,
          pageLimit: String(pageLimit),
          pageCursor: pageParam,
          useListingPrices,
        });

      return data;
    },
    refetchOnWindowFocus: false,
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor || undefined;
    },
    enabled,
  });

export const useWorkloadsGpuWasteCostImpactQuery = (
  props: UseWorkloadsGpuWasteCostImpactQueryProps
): UseWorkloadsGpuWasteCostImpactQuery => {
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery(getWorkloadsGpuWasteCostImpactQueryOptions(props));

  return {
    isLoading,
    data: flatMap(data?.pages, (page) => page?.workloads ?? []),
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    count: Number(data?.pages[0].count ?? 0),
  };
};
